import * as Sentry from "@sentry/react";
import type { AdminReporter } from "./admin-session";

const FEEDBACK_HOST_ID = "sentry-feedback";

let buffering: Promise<boolean> | null = null;
let feedback: Promise<boolean> | null = null;

function sentryEnabled(): boolean {
  return Sentry.getClient()?.getOptions().enabled !== false;
}

/**
 * Keeps the last minute of an admin's session in memory without uploading it,
 * so a bug that is hard to reproduce can be saved after it happens. Opening the
 * feedback form uploads it. Loaded on demand, so nobody else downloads the
 * replay code.
 */
export function startAdminReplayBuffer({
  impersonating,
}: {
  impersonating: boolean;
}): Promise<boolean> {
  buffering ??= loadReplay(impersonating);
  return buffering;
}

async function loadReplay(impersonating: boolean): Promise<boolean> {
  if (!sentryEnabled()) return false;

  try {
    const replayIntegration =
      await Sentry.lazyLoadIntegration("replayIntegration");
    // Masked while impersonating (a salon's real data); readable in an admin's own
    // session so a logic bug shows the wrong value. /admin lists every tenant and
    // carries `data-sentry-mask`; impersonating reloads, so this never carries over.
    Sentry.addIntegration(
      replayIntegration({ maskAllText: impersonating, blockAllMedia: true }),
    );

    const replay = Sentry.getReplay();
    if (replay && !replay.getReplayId()) replay.startBuffering();
    return true;
  } catch (error) {
    buffering = null;
    Sentry.captureException(error, {
      level: "warning",
      tags: { surface: "admin-replay" },
    });
    return false;
  }
}

/**
 * The replay buffer, then the bug-report button. The feedback bundle is far
 * smaller and would otherwise win the race, so a report filed in that window
 * would carry no replay. A replay that fails to load still gets the button.
 */
export async function startAdminReporting({
  impersonating,
}: {
  impersonating: boolean;
}): Promise<boolean> {
  await startAdminReplayBuffer({ impersonating });
  return startAdminFeedback();
}

/**
 * Sentry's bug-report button: a screenshot the reporter can highlight or
 * redact, sent with the buffered replay. Agents read both through the Sentry
 * MCP. Loaded on demand, like the replay.
 */
export function startAdminFeedback(): Promise<boolean> {
  feedback ??= loadFeedback();
  return feedback;
}

async function loadFeedback(): Promise<boolean> {
  if (!sentryEnabled()) return false;

  try {
    const feedbackIntegration = await Sentry.lazyLoadIntegration(
      "feedbackIntegration",
    );
    Sentry.addIntegration(
      feedbackIntegration({
        id: FEEDBACK_HOST_ID,
        // Opened from our own button (attachFeedbackButton); Sentry's is large
        // and fixed to the bottom-right corner.
        autoInject: false,
        colorScheme: "system",
        showBranding: false,
        // The buffer is only uploaded on submit, by which point time spent in
        // the form has pushed the bug out of it; save it as the form opens.
        onFormOpen: flushReplayBuffer,
        // The reporter's words are all an agent has besides the screenshot.
        messagePlaceholder:
          "What is wrong, and what did you expect instead? Highlight the spot on the screenshot.",
      }),
    );
    await exemptFormFromModals();
    return true;
  } catch (error) {
    feedback = null;
    Sentry.captureException(error, {
      level: "warning",
      tags: { surface: "admin-feedback" },
    });
    return false;
  }
}

function flushReplayBuffer(): void {
  Sentry.getReplay()
    ?.flush()
    .catch((error: unknown) =>
      Sentry.captureException(error, {
        level: "warning",
        tags: { surface: "admin-replay" },
      }),
    );
}

/**
 * An open HeroUI modal or drawer (react-aria) closes on a click outside it,
 * pulls focus back into itself, and makes anything added to the page inert —
 * so it would close as the form opens, or leave the form dead. This attribute
 * is react-aria's exception for top-layer UI like toasts.
 */
export const TOP_LAYER_ATTRIBUTE = "data-react-aria-top-layer";

// Sentry adds the form's host element once and reuses it. Building it now,
// before any modal is open, lets it carry the exception from the start; tagged
// only when the form opens, a modal would already have made it inert.
async function exemptFormFromModals(): Promise<void> {
  await Sentry.getFeedback()?.createForm();
  document
    .getElementById(FEEDBACK_HOST_ID)
    ?.setAttribute(TOP_LAYER_ATTRIBUTE, "true");
}

/** Opens Sentry's feedback form from `el`; returns the cleanup. */
export function attachFeedbackButton(el: HTMLElement): () => void {
  return Sentry.getFeedback()?.attachTo(el) ?? (() => {});
}

/**
 * Attributes the session to the admin driving it, even mid-impersonation, so
 * replays can be found by who was driving and the feedback form is prefilled.
 */
export function identifyAdmin(
  reporter: AdminReporter,
  impersonating: boolean,
): void {
  if (reporter.email) {
    Sentry.setTag("lime_admin", reporter.email);
    Sentry.setUser({ email: reporter.email, username: reporter.name });
  }
  Sentry.setTag("lime_admin_impersonating", impersonating ? "yes" : "no");
}
